<template>
  <!-- Full screen modal overlay -->
  <div v-if="modelValue" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <!-- Close button -->
      <button class="close-btn" @click="closeModal">
        <IconX :size="24" />
      </button>

      <!-- Modal content -->
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ __DICT__.scanQrCode }}</h5>
        </div>

        <div v-if="error" class="alert alert-danger m-2">
          {{ error }}
        </div>

        <div v-else class="scanner-container">
          <video
              ref="videoElement"
              playsinline
              class="scanner-video"
              :class="{ 'scanning-disabled': !canScan }"
          ></video>

          <!-- Centered start prompt overlay -->
          <div v-if="showStartPrompt" class="scanner-start-overlay">
            <div class="text-center">
              <button class="btn btn-primary" @click="startScanning">{{ __DICT__.enableCamera }}</button>
              <div class="text-muted mt-2" style="max-width: 280px; margin: 0 auto;">
                {{ __DICT__.qrScanner.cameraPermissionMessage }}
              </div>
            </div>
          </div>

          <div class="scanner-overlay"
               :class="{ 'scanning-disabled': !canScan }">
            <div class="scanner-frame"
                 :class="{ 'scanning': canScan, 'cooldown': !canScan, 'error': lastScanWasError }">
              <span></span>
            </div>
          </div>

          <div v-if="!canScan" class="scanner-message">
            <template v-if="lastScanError">
              {{ lastScanError }}
            </template>
            <template v-else>
              {{ __DICT__.qrScanner.pleaseWait }} {{ cooldownRemaining }}{{ __DICT__.qrScanner.secondsSuffix }}
            </template>
          </div>
        </div>
        <div class="p-2">
          <div v-if="scanresp" class="alert alert-important text-white" :class="getAlertClass(scanresp)" role="alert">
            <div class="d-flex">
              <div>
                {{ scanresp.message }}
                <span v-if="scanresp.ticket?.bookingRef"> - {{ scanresp.ticket.bookingRef }}</span>
                <div v-if="scanresp.ticket?.food === 1 && hasDietaryRequirements(scanresp.ticket?.dietaryRequirements)" class="mt-1 fw-bold">
                  {{ scanresp.ticket.dietaryRequirements }}
                </div>
                <div v-if="scanresp.offline" class="mt-1 text-white">
                  {{ __DICT__.scan.offlineNote }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="scanErr" class="alert alert-important alert-warning text-white bg-danger mt-3" role="alert">
            <div class="d-flex">
              <div>
                {{ scanErr.message }}
                <div v-if="scanErr.lastUsed" class="mt-1">
                  {{ formatLastUsed(scanErr.lastUsed) }}
                </div>
                <div v-if="!isOnline" class="mt-1 text-muted">
                  {{ __DICT__.scan.offlineCheck }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onBeforeUnmount, watch} from 'vue'
import { IconX } from '@tabler/icons-vue'
import jsQR from 'jsqr'
import { hasDietaryRequirements, formatLastUsed, validateBarcode } from '@/utils/scanUtils.js'
import { checkScannedCode } from '@/services/qrScan.js'
import { qrNoticeFor } from '@/utils/qrMessages.js'
import * as Sentry from '@sentry/vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  cooldownPeriod: {
    type: Number,
    default: 4000 // 4 seconds cooldown
  },
  scanInterval: {
    type: Number,
    default: 500 // Scan every 500ms
  },
  errorDisplayTime: {
    type: Number,
    default: 2000 // How long to show error messages
  },
  allowPartial: {
    type: Boolean,
    default: false
  },
  scanresp: {
    type: Object,
    default: null
  },
  scanErr: {
    type: Object,
    default: null
  },
  isOnline: {
    type: Boolean,
    default: true
  }
})

/**
 * code-scanned(reference, scan): reference is the bare code for local lookups;
 *   scan is { raw, reference, token, verification, mode } - send scan.raw as the
 *   code and qrRequestFields(scan) alongside it (utils/qrVerify.js).
 * qr-blocked(message, scan): enforce mode refused a bad signature locally.
 */
const emit = defineEmits(['update:modelValue', 'code-scanned', 'invalid-code', 'qr-blocked', 'close'])

// Refs
const videoElement = ref(null)
const canvasElement = ref(null)
const error = ref(null)
const videoStream = ref(null)
const canScan = ref(true)
const lastScanError = ref(null)
const lastScanWasError = ref(false)
const cooldownRemaining = ref(0)
const showStartPrompt = ref(false)
const permissionState = ref('prompt') // prompt | granted | denied | unknown
let scanTimer = null
let cooldownTimer = null

const closeModal = () => {
  emit('update:modelValue', false)
  emit('close')
  cleanup()
}

const getAlertClass = (response) => {
  if (response.offline) {
    return response.valid ? 'bg-azure' : 'bg-warning';
  }
  return response.queued ? 'bg-warning' : 'bg-success';
};


const showError = (errorMessage) => {
  lastScanError.value = errorMessage
  lastScanWasError.value = true
  canScan.value = false

  setTimeout(() => {
    lastScanError.value = null
    lastScanWasError.value = false
    canScan.value = true
  }, props.errorDisplayTime)
}

const checkPermissionsAndPrepare = async () => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      error.value = 'Camera is not supported by this browser.'
      showStartPrompt.value = false
      return
    }
    const isSecure = window.isSecureContext || location.protocol === 'https:'
    if (!isSecure) {
      error.value = 'Camera access requires HTTPS connection.'
      showStartPrompt.value = false
      return
    }
    let granted = false
    permissionState.value = 'unknown'
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const result = await navigator.permissions.query({ name: 'camera' })
        if (result && result.state) {
          permissionState.value = result.state
          granted = result.state === 'granted'
          result.onchange = () => { permissionState.value = result.state }
        }
      } catch (_) {
        permissionState.value = 'unknown'
      }
    }
    if (granted) {
      await startScanning()
      showStartPrompt.value = false
    } else {
      showStartPrompt.value = true
    }
  } catch (_) {
    showStartPrompt.value = true
  }
}

const startScanning = async () => {
  try {
    error.value = null

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    const videoConstraints = isIOS
        ? {
          facingMode: 'environment',
          width: {min: 320, ideal: 720, max: 1280},
          height: {min: 320, ideal: 720, max: 1280}
        }
        : {
          facingMode: 'environment',
          width: {ideal: 1280},
          height: {ideal: 1280}
        }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: videoConstraints
    })

    videoStream.value = stream
    const video = videoElement.value

    // Guard against video element being null (component unmounted or not in DOM)
    if (!video) {
      // Clean up the stream we just acquired since we can't use it
      stream.getTracks().forEach(track => track.stop())
      videoStream.value = null
      return
    }

    try { video.setAttribute('playsinline', 'true') } catch(_){}
    try { video.setAttribute('autoplay', '') } catch(_){}
    try { video.muted = true } catch(_){}
    video.srcObject = videoStream.value

    await video.play()

    scanTimer = setInterval(scanQRCode, props.scanInterval)

  } catch (err) {
    console.error('Camera access error:', err)

    const msg = String(err && (err.message || err.name) || '').toLowerCase()
    if (err && (err.name === 'NotAllowedError' || msg.includes('notallowederror') || msg.includes('permission denied') || msg.includes('request is not allowed'))) {
      lastScanError.value = 'Camera permission was denied. Please enable camera access in your browser settings and try again.'
      showStartPrompt.value = true
      error.value = null
      Sentry.addBreadcrumb({
        category: 'qr-scanner',
        message: 'Camera permission denied (modal)',
        level: 'warning'
      })
      return
    }

    error.value = err.message || 'Failed to access camera'

    // Capture unexpected errors in Sentry with additional context
    Sentry.captureException(err, {
      tags: {
        component: 'QrScanner',
        operation: 'startCamera'
      },
      extra: {
        userAgent: navigator.userAgent,
        isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      }
    })
  }
}

const scanQRCode = async () => {
  try {
    const video = videoElement.value
    if (!video || !canScan.value || !video.videoWidth || !video.videoHeight) return

    // Reuse the canvas and update dimensions if necessary
    if (!canvasElement.value) {
      canvasElement.value = document.createElement('canvas')
    }

    const width = video.videoWidth || 640
    const height = video.videoHeight || 480

    if (canvasElement.value.width !== width ||
        canvasElement.value.height !== height) {
      canvasElement.value.width = width
      canvasElement.value.height = height
    }

    try {
      const context = canvasElement.value.getContext('2d', {willReadFrequently: true})
      context.drawImage(video, 0, 0, canvasElement.value.width, canvasElement.value.height)


      const safeWidth = Math.min(canvasElement.value.width, 1280)
      const safeHeight = Math.min(canvasElement.value.height, 1280)
      const imageData = context.getImageData(0, 0, safeWidth, safeHeight)

      // Only proceed if we have valid image data
      if (imageData && imageData.data && imageData.width > 0 && imageData.height > 0) {
        const code = jsQR(imageData.data, imageData.width, imageData.height)

        if (code) {
          const validationResult = validateBarcode(code.data, props.allowPartial)

          if (validationResult.valid) {
            // Hold the scanner while the signature is checked, so the next
            // interval tick cannot read the same code again.
            canScan.value = false
            const { scan, decision } = await checkScannedCode(code.data)

            if (decision.block) {
              const message = qrNoticeFor(scan.verification, { block: true })?.text
              emit('qr-blocked', message, scan)
              showError(message)
              return
            }

            triggerCooldown()
            // The bare reference for the views' cached-manifest lookups; the
            // full string and verification ride along for the request body.
            emit('code-scanned', scan.reference, scan)
          } else {
            emit('invalid-code', validationResult.error)
            showError(validationResult.error)
          }
        }
      }
    } catch (imgError) {
      console.error('Error processing camera image:', imgError)
      Sentry.captureException(imgError, {
        tags: {
          component: 'QrScanner',
          operation: 'processImage'
        }
      })
    }
  } catch (err) {
    // Capture any errors that happen during scanning
    console.error('QR scan error:', err)

    Sentry.captureException(err, {
      tags: {
        component: 'QrScanner',
        operation: 'scanQRCode'
      },
      extra: {
        videoWidth: videoElement.value?.videoWidth,
        videoHeight: videoElement.value?.videoHeight,
        canvasPresent: !!canvasElement.value,
        canvasWidth: canvasElement.value?.width,
        canvasHeight: canvasElement.value?.height
      }
    })
  }
}

const triggerCooldown = () => {
  canScan.value = false
  cooldownRemaining.value = Math.ceil(props.cooldownPeriod / 1000)

  cooldownTimer = setInterval(() => {
    cooldownRemaining.value--
  }, 1000)

  setTimeout(() => {
    canScan.value = true
    cooldownRemaining.value = 0
    clearInterval(cooldownTimer)
  }, props.cooldownPeriod)
}

const cleanup = () => {
  try {
    Sentry.addBreadcrumb({
      category: 'qr-scanner',
      message: 'Cleaning up QR scanner',
      level: 'info'
    })

    if (videoStream.value) {
      const tracks = videoStream.value.getTracks()
      tracks.forEach(track => track.stop())
      videoStream.value = null
    }
    if (scanTimer) {
      clearInterval(scanTimer)
    }
    if (cooldownTimer) {
      clearInterval(cooldownTimer)
    }
    // Clean up canvas reference
    canvasElement.value = null

    Sentry.addBreadcrumb({
      category: 'qr-scanner',
      message: 'QR scanner cleanup completed',
      level: 'info'
    })
  } catch (err) {
    console.error('Error during cleanup:', err)

    Sentry.captureException(err, {
      tags: {
        component: 'QrScanner',
        operation: 'cleanup'
      }
    })
  }
}

// Watch for changes to the modelValue prop to start/stop the scanner
watch(() => props.modelValue, (isVisible) => {
  if (isVisible) {
    // Prepare permissions and show start prompt if needed
    checkPermissionsAndPrepare()
  } else {
    // Clean up when the modal is hidden
    cleanup()
  }
}, {immediate: true})

// Clean up on component unmount
onBeforeUnmount(() => {
  cleanup()
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  overflow: hidden;
}

.modal-container {
  position: relative;
  width: min(90vw, 90vh);
  max-width: 500px;
  max-height: 90vh;
  background-color: var(--tblr-bg-surface);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: var(--tblr-box-shadow-lg);
  display: flex;
  flex-direction: column;
}

.modal-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  border-bottom: 1px solid var(--tblr-border-color);
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1100;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: white;
  transform: scale(1.1);
}

.scanner-container {
  position: relative;
  aspect-ratio: 1/1;
  width: 100%;
  background-color: #000;
  overflow: hidden;
}

.scanner-video {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  overflow: hidden;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);

  /* Add scanning guide pattern */
  background-image: linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
  linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
}

.scanner-start-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  z-index: 2;
}

.scanner-frame {
  border: 2px solid white;
  border-radius: 4px;
  width: 60%;
  height: 60%;
  position: relative;
}

.scanner-frame::before,
.scanner-frame::after,
.scanner-frame > span::before,
.scanner-frame > span::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  border-color: #fff;
  border-style: solid;
  border-width: 0;
}

.scanner-frame::before {
  top: -2px;
  left: -2px;
  border-top-width: 4px;
  border-left-width: 4px;
}

.scanner-frame::after {
  top: -2px;
  right: -2px;
  border-top-width: 4px;
  border-right-width: 4px;
}

.scanner-frame > span::before {
  bottom: -2px;
  left: -2px;
  border-bottom-width: 4px;
  border-left-width: 4px;
}

.scanner-frame > span::after {
  bottom: -2px;
  right: -2px;
  border-bottom-width: 4px;
  border-right-width: 4px;
}

.scanner-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  text-align: center;
}

.scanning-disabled {
  filter: brightness(0.3);
  transition: filter 0.3s ease;
}

.scanning {
  border-color: white !important;
  animation: scan 2s infinite;
}

.cooldown {
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.error {
  border-color: #dc3545 !important;
  animation: error-flash 0.5s;
}

@keyframes scan {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

@keyframes error-flash {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }
}
</style>
